/**
 * 「AI 利用」画面の塊 3（内訳）。機能別 / workspace 別 / モデル別の表。
 *
 * 正本: `spec/sales-counter/screen-contracts/admin-ops-ai-usage.md` 塊 3。
 * - 並び: 選んだ指標（概算原価 / 件数）の降順。全件が単価未登録の行は最後（その中は件数の降順）。
 * - 機能別は全機能を常に並べ、0 件の機能は最下部（自動の 0 件は amber・U-12）。
 * - 行から個別の呼び出しを開く導線は作らない（守るライン #1）。
 */
import type {
  AiUsageAmount,
  AiUsageConfigured,
  AiUsageFeature,
  AiUsageModel,
  AiUsageWorkspace,
} from '../../lib/ai-usage-api';
import { costCell, formatInt, formatJpy, formatUsd, perCallCell } from './format';

export type SortKey = 'cost' | 'calls';

/** 概算原価 or 件数の降順。全件が単価未登録の行は最後、その中は件数の降順。 */
function byMetric<T extends AiUsageAmount>(sort: SortKey) {
  const unpriced = (a: T) => a.calls > 0 && a.unpricedCalls === a.calls;
  return (a: T, b: T) => {
    if (sort === 'calls') return b.calls - a.calls;
    if (unpriced(a) !== unpriced(b)) return unpriced(a) ? 1 : -1;
    return b.costUsd - a.costUsd || b.calls - a.calls;
  };
}

const TH = 'px-3 py-2 text-left text-xs font-medium text-slate-500';
const TD = 'px-3 py-2 text-sm text-slate-800';
const NUM = `${TD} text-right tabular-nums`;

/** 「単価未登録」は amber の文字で出す（State Matrix P2）。それ以外の金額はそのまま。 */
function Money({ text }: { text: string }) {
  if (text === '単価未登録') return <span className="text-amber-700">{text}</span>;
  return text;
}

function SortHeader({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <th className={`${TH} text-right`}>
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={active ? 'font-semibold text-slate-900' : 'text-slate-500 hover:text-slate-700'}
      >
        {label}
        {active ? ' ↓' : ''}
      </button>
    </th>
  );
}

function CostCell({ a, withJpy, rate }: { a: AiUsageAmount; withJpy: boolean; rate: number }) {
  const cell = costCell(a);
  return (
    <td className={NUM}>
      <div>
        <Money text={cell} />
      </div>
      {withJpy && cell !== '単価未登録' && (
        <div className="text-xs text-slate-500">{formatJpy(a.costUsd * rate)}</div>
      )}
      {/* 単価ありと未登録が混じるとき、金額が全体に見えないよう含まない件数を添える（P3）。 */}
      {a.unpricedCalls > 0 && a.unpricedCalls < a.calls && (
        <div className="text-xs text-amber-700">
          単価未登録の {formatInt(a.unpricedCalls)} 件を含みません
        </div>
      )}
    </td>
  );
}

function TriggerBadge({ trigger }: { trigger: AiUsageFeature['trigger'] }) {
  // 名称未登録の識別子は起動が分からないので「不明」（State Matrix D4）。
  const label = trigger === 'auto' ? '自動' : trigger === 'manual' ? '手動' : '不明';
  return (
    <span className="ml-2 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-600">
      {label}
    </span>
  );
}

function FeatureTable({
  data,
  sort,
  onSort,
}: {
  data: AiUsageConfigured;
  sort: SortKey;
  onSort: (s: SortKey) => void;
}) {
  const used = data.features.filter((f) => f.calls > 0).sort(byMetric<AiUsageFeature>(sort));
  // 0 件の機能も表から消さない（「無いこと」が見えるのが停止検知の床）。最下部に並べる。
  const idle = data.features.filter((f) => f.calls === 0);
  return (
    <table data-testid="ai-usage-features" className="w-full">
      <thead className="border-b border-slate-200">
        <tr>
          <th className={TH}>機能</th>
          <th className={TH}>モデル</th>
          <SortHeader label="件数" active={sort === 'calls'} onClick={() => onSort('calls')} />
          <th className={`${TH} text-right`}>入力 tok</th>
          <th className={`${TH} text-right`}>出力 tok</th>
          <th className={`${TH} text-right`}>1 件あたり</th>
          <SortHeader label="概算原価" active={sort === 'cost'} onClick={() => onSort('cost')} />
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {[...used, ...idle].map((f) => {
          const split = f.models.length > 1;
          return [
            <tr key={f.feature} data-testid="feature-row">
              <td className={TD}>
                <div>
                  {f.label ?? `${f.feature}（名称未登録）`}
                  <TriggerBadge trigger={f.trigger} />
                </div>
                <div className="font-mono text-xs text-slate-500">{f.feature}</div>
                {split && (
                  <div className="mt-1 text-xs text-slate-600">
                    期間内にモデルが変わっています。モデルごとに分けて表示しています。
                  </div>
                )}
              </td>
              <td className={`${TD} text-xs`}>
                {f.models.length === 1 ? (
                  <>
                    <div className="font-mono">{f.models[0]?.model}</div>
                    <div className="text-slate-500">{f.models[0]?.provider}</div>
                  </>
                ) : split ? (
                  `${f.models.length} モデル`
                ) : (
                  '—'
                )}
              </td>
              <td className={NUM}>
                {f.calls === 0 ? (
                  <span className={f.trigger === 'auto' ? 'text-amber-700' : 'text-slate-500'}>
                    {f.trigger === 'auto' ? '0 件（自動）' : '0 件'}
                  </span>
                ) : (
                  `${formatInt(f.calls)} 件`
                )}
              </td>
              <td className={NUM}>{formatInt(f.inputTokens)}</td>
              <td className={NUM}>{formatInt(f.outputTokens)}</td>
              <td className={NUM}>{f.calls === 0 ? '—' : <Money text={perCallCell(f)} />}</td>
              <CostCell a={f} withJpy={f.calls > 0} rate={data.rate.rate} />
            </tr>,
            ...(split
              ? f.models.map((m) => (
                  <tr
                    key={`${f.feature}/${m.model}`}
                    data-testid="feature-model-row"
                    className="bg-slate-50/60"
                  >
                    <td className={`${TD} pl-8 text-xs text-slate-500`}>
                      └ {m.firstDay} から
                      {m.calls < 10 && (
                        <span className="ml-2 text-amber-700">参考値（{m.calls} 件）</span>
                      )}
                    </td>
                    <td className={`${TD} font-mono text-xs`}>{m.model}</td>
                    <td className={NUM}>{formatInt(m.calls)} 件</td>
                    <td className={NUM}>
                      {formatInt(Math.round(m.inputTokens / Math.max(m.calls, 1)))} / 件
                    </td>
                    <td className={NUM}>
                      {formatInt(Math.round(m.outputTokens / Math.max(m.calls, 1)))} / 件
                    </td>
                    <td className={NUM}>
                      <Money text={perCallCell(m)} />
                    </td>
                    <CostCell a={m} withJpy={false} rate={data.rate.rate} />
                  </tr>
                ))
              : []),
          ];
        })}
      </tbody>
    </table>
  );
}

export function workspaceLabel(
  w: Pick<AiUsageWorkspace, 'workspaceId' | 'name' | 'slug' | 'deleted'>,
): string {
  if (w.deleted) return `削除済み workspace（${w.workspaceId.slice(0, 8)}）`;
  return w.name || w.slug || w.workspaceId.slice(0, 8);
}

function WorkspaceTable({
  data,
  sort,
  onSort,
  onSelect,
}: {
  data: AiUsageConfigured;
  sort: SortKey;
  onSort: (s: SortKey) => void;
  onSelect: (w: AiUsageWorkspace) => void;
}) {
  const rows = [...data.workspaces].sort(byMetric<AiUsageWorkspace>(sort));
  return (
    <table data-testid="ai-usage-workspaces" className="w-full">
      <thead className="border-b border-slate-200">
        <tr>
          <th className={TH}>workspace</th>
          <SortHeader label="件数" active={sort === 'calls'} onClick={() => onSort('calls')} />
          <th className={`${TH} text-right`}>入力 tok</th>
          <th className={`${TH} text-right`}>出力 tok</th>
          <SortHeader label="概算原価" active={sort === 'cost'} onClick={() => onSort('cost')} />
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((w) => (
          <tr key={w.workspaceId}>
            <td className={TD}>
              <button
                type="button"
                onClick={() => onSelect(w)}
                className="text-left hover:underline"
              >
                {workspaceLabel(w)}
              </button>
              {!w.deleted && (
                <div className="font-mono text-xs text-slate-500">{w.workspaceId.slice(0, 8)}</div>
              )}
            </td>
            <td className={NUM}>{formatInt(w.calls)} 件</td>
            <td className={NUM}>{formatInt(w.inputTokens)}</td>
            <td className={NUM}>{formatInt(w.outputTokens)}</td>
            <CostCell a={w} withJpy rate={data.rate.rate} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ModelTable({
  data,
  sort,
  onSort,
}: {
  data: AiUsageConfigured;
  sort: SortKey;
  onSort: (s: SortKey) => void;
}) {
  const rows = [...data.models].sort(byMetric<AiUsageModel>(sort));
  return (
    <table data-testid="ai-usage-models" className="w-full">
      <thead className="border-b border-slate-200">
        <tr>
          <th className={TH}>モデル</th>
          <th className={`${TH} text-right`}>単価（入 / 出）</th>
          <th className={TH}>使っている機能</th>
          <SortHeader label="件数" active={sort === 'calls'} onClick={() => onSort('calls')} />
          <th className={`${TH} text-right`}>入力 tok</th>
          <th className={`${TH} text-right`}>出力 tok</th>
          <SortHeader label="概算原価" active={sort === 'cost'} onClick={() => onSort('cost')} />
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((m) => (
          <tr key={m.model}>
            <td className={TD}>
              <div className="font-mono text-xs">{m.model}</div>
              <div className="text-xs text-slate-500">{m.provider}</div>
            </td>
            <td className={NUM}>
              <Money
                text={
                  m.price
                    ? `${formatUsd(m.price.inputUsdPerMTok)} / ${formatUsd(m.price.outputUsdPerMTok)}`
                    : '単価未登録'
                }
              />
            </td>
            <td className={`${TD} text-xs`}>{m.features.join('、')}</td>
            <td className={NUM}>{formatInt(m.calls)} 件</td>
            <td className={NUM}>{formatInt(m.inputTokens)}</td>
            <td className={NUM}>{formatInt(m.outputTokens)}</td>
            <CostCell a={m} withJpy rate={data.rate.rate} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export type Axis = 'feature' | 'workspace' | 'model';

export function AiUsageBreakdown(props: {
  data: AiUsageConfigured;
  axis: Axis;
  sort: SortKey;
  onSort: (s: SortKey) => void;
  onSelectWorkspace: (w: AiUsageWorkspace) => void;
}) {
  const { data, axis } = props;
  const empty = data.total.calls === 0;
  return (
    <>
      {empty && <p className="mb-3 text-sm text-slate-600">この期間の AI 呼び出しはありません。</p>}
      {axis === 'feature' && <FeatureTable data={data} sort={props.sort} onSort={props.onSort} />}
      {axis === 'workspace' && !empty && (
        <WorkspaceTable
          data={data}
          sort={props.sort}
          onSort={props.onSort}
          onSelect={props.onSelectWorkspace}
        />
      )}
      {axis === 'model' && !empty && (
        <ModelTable data={data} sort={props.sort} onSort={props.onSort} />
      )}
    </>
  );
}
